import { filter, includes, map, some } from 'lodash';
import type { GroupedUsersList } from '@halo/ui-chat-react';
import type { GroupInfo, User } from '@halo/chat-sdk-react';
import { StateEnum, useAppStore, GroupMemberRole, formatUser } from '@halo/chat-sdk-react';
import { useShallowEnhance } from '@halo/zustand-enhance';
import { isSessionCategory, SessionCategoryPrefix } from '@/store/client';

type List = GroupedUsersList & { key: GroupCreateType };
export enum GroupCreateType {
  MEMBER_CONTACTS = 'member-contacts',
  MEMBER_CHAT = 'member-chat',
  GROUP_CONTACTS = 'group-contacts',
  GROUP_CHAT = 'group-chat',
  GLOBAL = 'global',
}
export const useSearch = (searchValue: string, resetTrigger: number, isOpen: boolean) => {
  const value = useDeferredValue(searchValue);
  const [contactsUsers, setContactsUsers] = useState<List[]>([]);
  const [sessionUsers, setSessionUsers] = useState<List[]>([]);
  const [globalUsers, setGlobalUsers] = useState<List[]>([]);
  const [contactsGroups, setContactsGroups] = useState<List[]>([]);
  const [sessionGroup, setSessionGroup] = useState<List[]>([]);
  const { client, sessions } = useAppStore(useShallowEnhance(['client', 'sessions']));
  const { t } = useTranslation();

  const list = useMemo(() => {
    return [
      ...contactsUsers,
      ...sessionUsers,
      ...contactsGroups,
      ...sessionGroup,
      ...globalUsers,
    ];
  }, [contactsUsers, sessionUsers, contactsGroups, sessionGroup, globalUsers]);

  const { run: onSearch } = useDebounceFn(
    async () => {
      const contactsRes = await client?.Api.contacts.contactsSearchByKeyInLocal(value);
      const filteredContacts = contactsRes?.users?.filter(
        (item) => item.state !== StateEnum.DELETE_ACCOUNT
      );
      setContactsUsers([
        {
          label: t('chat.membersInContacts'),
          items: filteredContacts ?? [],
          key: GroupCreateType.MEMBER_CONTACTS,
        },
      ]);
      let filteredSession: User[] = [];
      /**groups search */
      const contactGroups =
        (await client?.Api.contacts.contactsSearchGroupByKeyInLocal(value)) || [];
      const groups_ = await Promise.all(
        map(contactGroups, async (item) => {
          const result = await client?.Api.session.getMyGroupRole(item.gid).catch((_) => null);
          return {
            ...item,
            memberCount: item?.member_num || 0,
            isGroupAdmin:
              result?.role === GroupMemberRole.OWNER || result?.role === GroupMemberRole.ADMIN,
            isGroupOwner: result?.role === GroupMemberRole.OWNER,
          };
        })
      );
      const groupContacts = map(
        filter(groups_, (item) => item.isGroupAdmin || item.isGroupOwner),
        (item) => ({
          ...item,
          user_id: item.gid,
          have_group_key: true,
          have_user_key: true,
          isGroup: true,
        })
      );
      setContactsGroups([
        {
          label: t('filter.groupsInContacts'),
          items: groupContacts,
          key: GroupCreateType.GROUP_CONTACTS,
        },
      ]);
      if (value) {
        const sessionRes = await client?.Api.session.sessionSearchByKey(value, true);
        filteredSession =
          sessionRes?.filter(
            (item) =>
              !filteredContacts?.some((contact) => contact.user_id === item.user_id) &&
              item.state !== StateEnum.DELETE_ACCOUNT
          ) ?? [];
        setSessionUsers([
          {
            label: t('chat.membersInChats'),
            items: filteredSession.map((item) => formatUser(item)),
            key: GroupCreateType.MEMBER_CHAT,
          },
        ]);
        // chats groups filter contacts groups
        const filteredSessions = filter(
          sessions,
          (item) =>
            isSessionCategory(item.chat_id, SessionCategoryPrefix.Group) &&
            !some(groupContacts, { gid: item.chat_id }) &&
            Boolean(item.name)
        );
        let groupInfos: any = await Promise.all(
          filteredSessions.map(async (item) => {
            try {
              const result = (await client?.Api.session.getGroupInfoAwait(
                item.chat_id
              )) as GroupInfo;
              return {
                ...result,
                user_id: result.gid,
                have_group_key: true,
                have_user_key: true,
                isGroup: true,
              };
            } catch (_) {
              return false;
            }
          })
        );
        groupInfos = filter(groupInfos, Boolean);
        const _chatGroups = filter(groupInfos ?? [], (item) => {
          const word = value?.toLowerCase();
          return (
            includes(item.gid?.toLowerCase(), word) ||
            includes(item.group_name?.toLowerCase(), word) ||
            includes(
              `https://${import.meta.env.VITE_LANDING_HOST}/${item?.group_id}`.toLowerCase(),
              word
            )
          );
        });
        setSessionGroup([
          {
            label: t('chat.groupsInChats'),
            items: _chatGroups,
            key: GroupCreateType.GROUP_CHAT,
          },
        ]);
        // global search
        const globalRes = await client?.Api.contacts.contactsSearchByBeyInGlobal(value);
        const filteredGlobal =
          globalRes?.filter((item) => {
            return (
              !filteredSession?.some((session) => session.user_id === item.user_id) &&
              !filteredContacts?.some((contact) => contact.user_id === item.user_id)
            );
          }) ?? [];

        setGlobalUsers([
          {
            label: t('chat.membersInGlobal'),
            items: filteredGlobal,
            key: GroupCreateType.GLOBAL,
          },
        ]);
      } else {
        setSessionUsers([
          { label: t('chat.membersInChats'), items: [], key: GroupCreateType.MEMBER_CHAT },
        ]);
        setSessionGroup([
          {
            label: t('chat.groupsInChats'),
            items: [],
            key: GroupCreateType.GROUP_CHAT,
          },
        ]);
      }
    },
    { wait: 300 }
  );

  useEffect(() => {
    if (isOpen) {
      onSearch();
    }
  }, [value, onSearch, isOpen]);

  useEffect(() => {
    if (resetTrigger !== undefined) {
      setContactsUsers([]);
      setSessionUsers([]);
      setContactsGroups([]);
      setSessionGroup([]);
      setGlobalUsers([]);
    }
  }, [resetTrigger]);

  return { onSearch, contactsUsers, sessionUsers, list };
};
